import { type FC, useState, useEffect, useRef } from 'react';
import clsx from 'clsx';

import { RiskOverviewPanel } from '@/components/panels/RiskOverviewPanel';
import { NewsFeedPanel } from '@/components/panels/NewsFeedPanel';
import { AsiaIntelPanel } from '@/components/panels/AsiaIntelPanel';
import { MarketPanel } from '@/components/panels/MarketPanel';
import { VideoNewsPanel } from '@/components/panels/VideoNewsPanel';
import { LiveCamPanel } from '@/components/panels/LiveCamPanel';
import { CyberPanel } from '@/components/panels/CyberPanel';
import { SocialSignalPanel } from '@/components/panels/SocialSignalPanel';
import { EconomicCalendarPanel } from '@/components/panels/EconomicCalendarPanel';
import { SanctionsPanel } from '@/components/panels/SanctionsPanel';
import { DronePanel } from '@/components/panels/DronePanel';
import { CountryIntelPanel } from '@/components/panels/CountryIntelPanel';
import { EventTimelinePanel } from '@/components/panels/EventTimelinePanel';
import { NaturalDisasterPanel } from '@/components/panels/NaturalDisasterPanel';
import { SemiconductorPanel } from '@/components/panels/SemiconductorPanel';

// ---------------------------------------------------------------------------
// Panel registry
// ---------------------------------------------------------------------------

const FULL_PANELS: { key: string; label: string; Component: FC; height: string }[] = [
  { key: 'video-news', label: 'Live News', Component: VideoNewsPanel, height: 'h-[450px]' },
  { key: 'live-cam',   label: 'Live Cams', Component: LiveCamPanel,   height: 'h-[455px]' },
];

const GRID_PANELS: { key: string; label: string; Component: FC }[] = [
  { key: 'risk-overview',     label: 'Risk Overview',     Component: RiskOverviewPanel },
  { key: 'social-signals',    label: 'Social Signals',    Component: SocialSignalPanel },
  { key: 'event-timeline',    label: 'Event Timeline',    Component: EventTimelinePanel },
  { key: 'news-feed',         label: 'News Feed',         Component: NewsFeedPanel },
  { key: 'natural-disasters', label: 'Natural Disasters', Component: NaturalDisasterPanel },
  { key: 'cyber',             label: 'Cyber Intel',       Component: CyberPanel },
  { key: 'economic',          label: 'Economic Calendar', Component: EconomicCalendarPanel },
  { key: 'asia-intel',        label: 'Asia Intel',        Component: AsiaIntelPanel },
  { key: 'sanctions',         label: 'Sanctions',         Component: SanctionsPanel },
  { key: 'drone',             label: 'Drone Activity',    Component: DronePanel },
  { key: 'country-intel',     label: 'Country Intel',     Component: CountryIntelPanel },
  { key: 'semiconductor',     label: 'Semiconductor',     Component: SemiconductorPanel },
  { key: 'market',            label: 'Market Pulse',      Component: MarketPanel },
];

const ALL_PANELS = [
  ...FULL_PANELS,
  ...GRID_PANELS.map(p => ({ ...p, height: 'h-[280px]' })),
];

// ---------------------------------------------------------------------------
// 카테고리 탭 — 패널을 그룹화해서 한 번에 적은 수만 크게 표시
// ---------------------------------------------------------------------------

const PANEL_TABS: { key: string; label: string; panels: string[] }[] = [
  { key: 'all',     label: 'All',     panels: [] }, // special: 기존 전체 그리드
  { key: 'live',    label: 'Live',    panels: ['video-news', 'live-cam'] },
  { key: 'intel',   label: 'Intel',   panels: ['risk-overview', 'event-timeline', 'news-feed', 'social-signals', 'asia-intel', 'country-intel'] },
  { key: 'threats', label: 'Threats', panels: ['natural-disasters', 'cyber', 'sanctions', 'drone'] },
  { key: 'markets', label: 'Markets', panels: ['market', 'economic', 'semiconductor'] },
];

const TAB_STORAGE_KEY = 'dx-panel-tab';

// ---------------------------------------------------------------------------
// Per-panel collapse + fullscreen buttons (appear on hover, top-right corner)
// ---------------------------------------------------------------------------
const PanelControls: FC<{
  onCollapse: () => void;
  onFullscreen: () => void;
  collapsed: boolean;
  showFullscreen?: boolean;
}> = ({ onCollapse, onFullscreen, collapsed, showFullscreen = true }) => (
  <div className="absolute bottom-1 right-1 flex gap-0.5 z-20 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-150 pointer-events-none">
    {showFullscreen && (
      <button
        onClick={(e) => { e.stopPropagation(); onFullscreen(); }}
        className="w-5 h-5 flex items-center justify-center rounded bg-slate-800/90 hover:bg-slate-600 text-slate-400 hover:text-white border border-slate-700/60 transition-colors pointer-events-auto"
        title="Fullscreen"
      >
        <svg viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.4" width="9" height="9">
          <path d="M1 3.5V1h2.5M6.5 1H9v2.5M9 6.5V9H6.5M3.5 9H1V6.5" />
        </svg>
      </button>
    )}
    <button
      onClick={(e) => { e.stopPropagation(); onCollapse(); }}
      className="w-5 h-5 flex items-center justify-center rounded bg-slate-800/90 hover:bg-slate-600 text-slate-400 hover:text-white border border-slate-700/60 transition-colors pointer-events-auto"
      title={collapsed ? 'Expand' : 'Collapse'}
    >
      <svg viewBox="0 0 10 8" fill="none" stroke="currentColor" strokeWidth="1.5" width="9" height="7">
        {collapsed
          ? <polyline points="1,2 5,6 9,2" />
          : <polyline points="1,6 5,2 9,6" />}
      </svg>
    </button>
  </div>
);

// ---------------------------------------------------------------------------
// Main panel grid with whole-grid collapse + per-panel collapse/fullscreen
// ---------------------------------------------------------------------------
// Video panels use native browser fullscreen; others use custom overlay
const VIDEO_PANEL_KEYS = new Set(['video-news', 'live-cam']);

export const PanelGrid: FC = () => {
  // Auto-collapse on mobile (< 768px)
  const [gridCollapsed, setGridCollapsed] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 768,
  );
  const [collapsedPanels, setCollapsedPanels] = useState<Set<string>>(new Set());
  const [fullscreenKey, setFullscreenKey] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>(() => {
    if (typeof window === 'undefined') return 'intel';
    const saved = localStorage.getItem(TAB_STORAGE_KEY);
    return PANEL_TABS.some(t => t.key === saved) ? saved! : 'intel';
  });
  const panelRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const selectTab = (key: string) => {
    setActiveTab(key);
    try { localStorage.setItem(TAB_STORAGE_KEY, key); } catch { /* ignore */ }
  };

  const isCollapsed = (key: string) => collapsedPanels.has(key);

  const toggleCollapse = (key: string) =>
    setCollapsedPanels(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  const handleFullscreen = (key: string) => {
    if (VIDEO_PANEL_KEYS.has(key)) {
      // Native browser fullscreen for video panels
      const el = panelRefs.current[key];
      el?.requestFullscreen?.().catch(() => setFullscreenKey(key));
    } else {
      setFullscreenKey(key);
    }
  };

  // Esc closes custom fullscreen overlay
  useEffect(() => {
    if (!fullscreenKey) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setFullscreenKey(null); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [fullscreenKey]);

  const fullscreenPanel = ALL_PANELS.find(p => p.key === fullscreenKey);

  return (
    <>
      {/* ── Mobile backdrop (tap to close panels) ───────────────────────────── */}
      {!gridCollapsed && (
        <div
          className="fixed inset-0 z-[29] bg-black/50 md:hidden"
          onClick={() => setGridCollapsed(true)}
        />
      )}

      {/* ── Mobile side-tab (right edge, vertically centered) ──────────────── */}
      <button
        onClick={() => setGridCollapsed(c => !c)}
        className={clsx(
          'fixed right-0 top-1/2 -translate-y-1/2 z-[36] flex md:hidden',
          'flex-col items-center justify-center gap-0.5',
          'h-20 w-8 rounded-l-lg border border-r-0',
          'shadow-lg shadow-black/40 cursor-pointer transition-colors select-none',
          gridCollapsed
            ? 'bg-surface-panel/95 backdrop-blur-sm border-surface-border text-slate-300 hover:bg-surface-hover'
            : 'bg-red-950/90 backdrop-blur-sm border-red-700/80 text-red-300',
        )}
      >
        <span className="text-[9px] font-bold text-slate-500 tracking-wider">PNL</span>
        <span className="text-sm leading-none">📊</span>
        <span className="text-[13px] font-bold leading-none">{gridCollapsed ? '‹' : '›'}</span>
      </button>

      {/* ── Expand overlay — 70% 모달, 배경은 블러 처리된 기존 화면 ─────────── */}
      {fullscreenPanel && (() => {
        const Comp = fullscreenPanel.Component;
        return (
          <div
            className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50 backdrop-blur-sm"
            onClick={() => setFullscreenKey(null)}
          >
            <div
              className="glass-panel hud-corners flex flex-col w-[70vw] h-[70vh] max-w-[1100px] shadow-2xl shadow-black/60 overflow-hidden animate-slide-up"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-2 border-b border-surface-border shrink-0 bg-surface-panel/90">
                <span className="font-mono text-[11px] font-bold uppercase tracking-widest text-neon-cyan">
                  {fullscreenPanel.label}
                </span>
                <button
                  onClick={() => setFullscreenKey(null)}
                  className="w-7 h-7 flex items-center justify-center rounded border border-surface-border hover:border-neon-red/60 text-slate-400 hover:text-neon-red hover:bg-red-950/30 transition-colors text-base leading-none"
                  title="Close (Esc)"
                >✕</button>
              </div>
              <div className="flex-1 overflow-hidden">
                <Comp />
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Whole-grid collapse tab (hidden on mobile, shown md+) ───────────── */}
      <button
        onClick={() => setGridCollapsed(c => !c)}
        className={clsx(
          'absolute top-1/2 -translate-y-1/2 z-[35] h-14 w-5',
          'hidden md:flex items-center justify-center',
          'bg-surface-panel/90 backdrop-blur-sm',
          'border border-r-0 border-surface-border rounded-l',
          'text-slate-400 hover:text-slate-200 transition-all duration-300 pointer-events-auto',
          gridCollapsed ? 'md:right-0' : 'md:right-[42%] lg:right-[38%]',
        )}
        title={gridCollapsed ? 'Show panels' : 'Hide panels'}
      >
        <svg viewBox="0 0 6 10" fill="none" stroke="currentColor" strokeWidth="1.6" width="6" height="10">
          {gridCollapsed
            ? <polyline points="5,1 1,5 5,9" />
            : <polyline points="1,1 5,5 1,9" />}
        </svg>
      </button>

      {/* ── Panel aside ─────────────────────────────────────────────────────── */}
      <aside className={clsx(
        'absolute top-0 right-0 bottom-7',
        'w-full md:w-[42%] lg:w-[38%]',
        'pointer-events-none z-30 overflow-hidden',
        'transition-transform duration-300 ease-in-out',
        gridCollapsed && 'translate-x-full',
      )}>
        <div className="h-full flex flex-col pointer-events-auto">

          {/* ── Category tab bar ─────────────────────────────────────────── */}
          <div className="flex gap-1 px-2 pt-2 pb-1.5 shrink-0">
            {PANEL_TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => selectTab(tab.key)}
                className={clsx(
                  'flex-1 py-1.5 rounded font-mono text-[10px] font-bold uppercase tracking-wider transition-colors border',
                  activeTab === tab.key
                    ? 'bg-surface-hover border-neon-cyan/60 text-neon-cyan shadow-glow-sm'
                    : 'bg-surface-panel/80 backdrop-blur-sm border-surface-border text-slate-500 hover:text-slate-200 hover:bg-surface-hover',
                )}
              >
                <span className={clsx(activeTab === tab.key ? 'text-neon-cyan/50' : 'text-slate-600')}>::</span>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto px-2 pb-4 scrollbar-thin">

            {activeTab === 'all' ? (
              <>
                {/* Full-width panels (VideoNews, LiveCam) */}
                {FULL_PANELS.map(({ key, Component, height }, idx) => {
                  const collapsed = isCollapsed(key);
                  return (
                    <div
                      key={key}
                      ref={el => { panelRefs.current[key] = el; }}
                      className={clsx(
                        'glass-panel hud-corners mb-2 animate-slide-up shrink-0 relative group overflow-hidden',
                        collapsed ? 'h-9' : height,
                      )}
                      style={{
                        animationDelay: `${idx * 20}ms`,
                        animationFillMode: 'backwards',
                        transition: 'height 0.3s ease',
                      }}
                    >
                      <PanelControls
                        onCollapse={() => toggleCollapse(key)}
                        onFullscreen={() => handleFullscreen(key)}
                        collapsed={collapsed}
                        showFullscreen={false}
                      />
                      <Component />
                    </div>
                  );
                })}

                {/* 2-col grid panels */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                  {GRID_PANELS.map(({ key, Component }, idx) => {
                    const collapsed = isCollapsed(key);
                    return (
                      <div
                        key={key}
                        className={clsx(
                          'glass-panel hud-corners animate-slide-up relative group overflow-hidden',
                          collapsed ? 'h-9' : 'h-[280px]',
                        )}
                        style={{
                          animationDelay: `${(idx + 2) * 20}ms`,
                          animationFillMode: 'backwards',
                          transition: 'height 0.3s ease',
                        }}
                      >
                        <PanelControls
                          onCollapse={() => toggleCollapse(key)}
                          onFullscreen={() => handleFullscreen(key)}
                          collapsed={collapsed}
                        />
                        <Component />
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              /* 탭 모드 — 해당 카테고리 패널만 1열 크게 표시 */
              <div className="flex flex-col gap-2.5">
                {(PANEL_TABS.find(t => t.key === activeTab)?.panels ?? []).map((panelKey, idx) => {
                  const panel = ALL_PANELS.find(p => p.key === panelKey);
                  if (!panel) return null;
                  const { key, Component } = panel;
                  const isVideo = VIDEO_PANEL_KEYS.has(key);
                  const collapsed = isCollapsed(key);
                  return (
                    <div
                      key={key}
                      ref={el => { panelRefs.current[key] = el; }}
                      className={clsx(
                        'glass-panel hud-corners animate-slide-up shrink-0 relative group overflow-hidden',
                        collapsed ? 'h-9' : (isVideo ? panel.height : 'h-[360px]'),
                      )}
                      style={{
                        animationDelay: `${idx * 20}ms`,
                        animationFillMode: 'backwards',
                        transition: 'height 0.3s ease',
                      }}
                    >
                      <PanelControls
                        onCollapse={() => toggleCollapse(key)}
                        onFullscreen={() => handleFullscreen(key)}
                        collapsed={collapsed}
                        showFullscreen={!isVideo}
                      />
                      <Component />
                    </div>
                  );
                })}
              </div>
            )}

          </div>
        </div>
      </aside>
    </>
  );
};
